import routes from 'routes'

// Which pages get a slot in the mobile bottom bar, and which fall into More.
//
// Four fixed destinations, the same four for a coach and for a player:
// Dashboard, Schedule, Assignments, Teams. The bar is the same navigation for
// both roles, so it keeps the same shape for both; what differs is only which of
// the four a viewer is allowed to reach, and everything else they can reach goes
// into More.
//
// Matched by concrete route, not by position, so the order each role's sidebar
// happens to declare its items in cannot reshuffle the bar.

/**
 * Assignments resolves to a different page per role — a player opens their own
 * assignments, a coach opens the ones they hand out — so the slot accepts either
 * and takes whichever the viewer actually has.
 */
const SLOT_HREFS: string[][] = [
  [routes.dashboard.value],
  [routes.schedule.value],
  [routes.playerAssignments.value, routes.plans.trainingAssignments.value],
  [routes.teams.value]
]

export interface FooterSlots<T> {
  footerItems: T[]
  overflowItems: T[]
}

/**
 * Splits the visible items into the fixed bar and the More overflow.
 *
 * Any slot the viewer cannot reach is simply absent — the bar closes up rather
 * than backfilling from More, because a slot that holds a different page for
 * different people is a slot nobody can learn.
 */
export function computeFooterSlots<T extends { href: string }>(visibleItems: T[]): FooterSlots<T> {
  const footerItems: T[] = []
  for (const hrefs of SLOT_HREFS) {
    const found = visibleItems.find(item => hrefs.includes(item.href) && !footerItems.includes(item))
    if (found) footerItems.push(found)
  }

  const rest = visibleItems.filter(item => !footerItems.includes(item))

  // A More holding exactly one page is a menu the viewer has to open to find a
  // single link. That page takes the fifth slot itself and More disappears — it
  // only earns a slot once there is a choice behind it.
  if (rest.length === 1) return { footerItems: [...footerItems, rest[0]], overflowItems: [] }

  return { footerItems, overflowItems: rest }
}
